import { countWithBreaks } from "@/lib/tool-kit/admissions/counts";

/**
 * The UCAS undergraduate personal statement for 2026 entry onwards.
 *
 * Source: UCAS, "How to write your personal statement: 2026 entry onwards"
 * (ucas.com, checked October 2026). The statement is three answers sharing one budget of
 * 4,000 characters including spaces, each answer at least 350 characters. UCAS does not
 * say how a line break is counted and does not mention the 47-line limit of the old
 * single-essay format, so the counter shows a line break as one character and lets the
 * reader see the total if the form counts it as two.
 */

export const UCAS_LIMIT = 4000;
export const UCAS_MINIMUM = 350;

export const UCAS_QUESTIONS = ["q1", "q2", "q3"] as const;
export type UcasQuestion = (typeof UCAS_QUESTIONS)[number];
export type UcasAnswers = Record<UcasQuestion, string>;

export const UCAS_PRESETS = ["even", "subject", "experience"] as const;
export type UcasPreset = (typeof UCAS_PRESETS)[number];

/**
 * Starting budgets for a plan — this site's suggestions, not UCAS's: UCAS lets you
 * divide the 4,000 characters however you like. Each preset adds up to the limit.
 */
export const UCAS_BUDGETS: Record<UcasPreset, Record<UcasQuestion, number>> = {
  even: { q1: 1400, q2: 1300, q3: 1300 },
  subject: { q1: 1800, q2: 1200, q3: 1000 },
  experience: { q1: 1200, q2: 1100, q3: 1700 },
};

export type AnswerState = "empty" | "short" | "ok";

export interface AnswerCount {
  question: UcasQuestion;
  characters: number;
  /** Characters still needed to reach the minimum, 0 once reached. */
  shortfall: number;
  state: AnswerState;
}

export interface UcasAnalysis {
  answers: AnswerCount[];
  total: number;
  /** Characters left in the shared budget; negative when over. */
  remaining: number;
  over: boolean;
  /** Every answer has at least the minimum. */
  allMinimumsMet: boolean;
  /** Within the limit and every minimum met. */
  ready: boolean;
}

function stateOf(characters: number): AnswerState {
  if (characters === 0) {
    return "empty";
  }
  return characters < UCAS_MINIMUM ? "short" : "ok";
}

/** Count the three answers against the shared limit and each minimum. */
export function analyseUcas(
  answers: UcasAnswers,
  lineBreakCost: 1 | 2
): UcasAnalysis {
  const counts: AnswerCount[] = UCAS_QUESTIONS.map((question) => {
    const characters = countWithBreaks(answers[question], lineBreakCost);
    return {
      question,
      characters,
      shortfall: Math.max(0, UCAS_MINIMUM - characters),
      state: stateOf(characters),
    };
  });
  const total = counts.reduce((sum, answer) => sum + answer.characters, 0);
  const allMinimumsMet = counts.every((answer) => answer.state === "ok");
  return {
    answers: counts,
    total,
    remaining: UCAS_LIMIT - total,
    over: total > UCAS_LIMIT,
    allMinimumsMet,
    ready: allMinimumsMet && total <= UCAS_LIMIT,
  };
}

/** How the budget a plan allots compares with what is written for one answer. */
export function budgetGap(characters: number, budget: number): number {
  return budget - characters;
}

export interface UcasExportLabels {
  questions: Readonly<Record<UcasQuestion, string>>;
  notes: string;
  answer: string;
  budget: string;
  /** "{count} characters" with the number already formatted. */
  characters: (count: number) => string;
}

/**
 * Everything on the page as one text file: for each question its wording, the plan
 * notes, the budget and the draft. The drafts are what you paste into UCAS one box at a
 * time; the file keeps the three together so none is lost.
 */
export function exportUcas(
  answers: UcasAnswers,
  notes: UcasAnswers,
  budgets: Readonly<Record<UcasQuestion, number>>,
  labels: UcasExportLabels,
  lineBreakCost: 1 | 2
): string {
  return UCAS_QUESTIONS.map((question) => {
    const draft = answers[question].trim();
    const note = notes[question].trim();
    const lines = [
      labels.questions[question],
      `${labels.budget}: ${labels.characters(budgets[question])}`,
    ];
    if (note !== "") {
      lines.push("", `${labels.notes}:`, note);
    }
    lines.push(
      "",
      `${labels.answer} (${labels.characters(countWithBreaks(draft, lineBreakCost))}):`,
      draft
    );
    return lines.join("\n");
  }).join("\n\n\n");
}
